import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import { Capacitor } from '@capacitor/core';
import { Home, Clock3, MessageSquare, User } from 'lucide-react';
import { isIOS, isAndroid, isNativeApp, getPlatform } from '@/lib/capacitor';
import { checkPermissionStatus, requestNotificationPermission } from '@/lib/push-notifications';
import { getUserStorage, getLastUserId } from '@/lib/user-storage';

interface AppLayoutProps {
  children: React.ReactNode;
}

// Storage key for tracking if we've prompted for notifications
const NOTIFICATION_PROMPT_KEY = 'notification_prompt_shown';

// AppLayout handles platform-specific styling like safe area insets
// and auto-prompts for notification permission on first native app launch
export function AppLayout({ children }: AppLayoutProps) {
  const permissionPromptAttempted = useRef(false);
  const [location] = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Platform detection and styling
  useEffect(() => {
    // Debug logging for platform detection
    const platform = getPlatform();
    const native = isNativeApp();
    const capacitorPlatform = Capacitor.getPlatform();
    const capacitorNative = Capacitor.isNativePlatform();
    
    console.log('[AppLayout] Platform detection:', {
      platform,
      native,
      capacitorPlatform,
      capacitorNative,
      isAndroid: isAndroid(),
      isIOS: isIOS(),
    });
    
    if (!native) {
      console.log('[AppLayout] Not a native app, skipping platform-specific styling');
      return;
    }
    
    // Add platform-specific class to body for safe area handling
    if (isIOS()) {
      document.body.classList.add('ios-native-app');
      console.log('[AppLayout] iOS native app detected - enabling safe area insets');
    } else if (isAndroid()) {
      document.body.classList.add('android-native-app');
      console.log('[AppLayout] Android native app detected - enabling navigation bar padding');
    }
    
    return () => {
      document.body.classList.remove('ios-native-app');
      document.body.classList.remove('android-native-app');
    };
  }, []);
  
  // Auto-prompt for notification permission on first native app launch
  useEffect(() => {
    if (permissionPromptAttempted.current) return;
    
    async function promptForNotifications() {
      if (!isNativeApp()) return;
      
      permissionPromptAttempted.current = true;
      
      // Check if we've already shown the prompt for this user
      const userId = getLastUserId();
      const userStorage = getUserStorage(userId);
      const promptShown = localStorage.getItem(`${NOTIFICATION_PROMPT_KEY}_${userId || 'anon'}`);
      
      if (promptShown) {
        console.log('[AppLayout] Notification prompt already shown for this user');
        return;
      }
      
      // Check current permission status
      const status = await checkPermissionStatus();
      console.log('[AppLayout] Notification permission status:', status);
      
      if (status === 'prompt' || status === 'prompt-with-rationale') {
        // Request permission - this shows the native OS dialog
        console.log('[AppLayout] Auto-requesting notification permission');
        const result = await requestNotificationPermission();
        console.log('[AppLayout] Notification permission result:', result);
        
        // Mark that we've shown the prompt
        localStorage.setItem(`${NOTIFICATION_PROMPT_KEY}_${userId || 'anon'}`, 'true');
      } else if (status === 'granted') {
        console.log('[AppLayout] Notifications already granted');
      } else {
        console.log('[AppLayout] Notifications denied or unavailable');
      }
    }
    
    // Small delay to let the app settle before prompting
    const timer = setTimeout(promptForNotifications, 1500);
    return () => clearTimeout(timer);
  }, []);

  const showAndroidBar = isNativeApp() && isAndroid();

  return (
    <div>
      <div className={`overflow-x-hidden ${showAndroidBar ? "pb-28" : "pb-16"}`}>
        {children}
      </div>
      <BottomNavBar />
      {showAndroidBar && (
        <div
          className="android-nav-footer"
          aria-hidden="true"
          data-testid="android-nav-footer"
        />
      )}
    </div>
  );
}

function BottomNavBar() {
  const [location] = useLocation();

  const tabs = [
    { id: "home", label: "Home", icon: Home, href: "/app" },
    { id: "today", label: "Today", icon: Clock3, href: "/app/clock" },
    { id: "messages", label: "Messages", icon: MessageSquare, href: "/app/messages" },
    { id: "profile", label: "Profile", icon: User, href: "/app/profile" },
  ];

  const isActive = (href: string) => {
    if (href === "/app") return location === "/app" || location === "/" || location === "/app/";
    return location.startsWith(href);
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="bottom-nav"
    >
      <div className="flex items-center justify-around h-14">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const active = isActive(tab.href);
          return (
            <Link key={tab.id} href={tab.href}>
              <button
                className={`flex flex-col items-center justify-center gap-0.5 w-16 h-full transition-colors ${
                  active ? 'text-primary' : 'text-muted-foreground'
                }`}
                data-testid={`nav-${tab.id}`}
              >
                <Icon className="h-5 w-5" />
                {active && <span className="w-1 h-1 rounded-full bg-primary" />}
                <span className="text-[10px] font-medium">{tab.label}</span>
              </button>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
